/**
 * A react-admin `<Datagrid>` on a wide screen and a list of cards on a phone
 * (hybrid-3c §12): one card per row, the identifying column first with its
 * status at the right, then two to four label/value lines, and the row's
 * actions behind a ghost `MoreVert`. A tap on the card does what a row click
 * does on desktop. The empty state is `DatagridEmpty` in both forms.
 */

import React from 'react';
import { Datagrid, DatagridProps, RaRecord, useListContext } from 'react-admin';
import { Box, Card, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DatagridEmpty } from './DatagridEmpty';

export interface MobileCardLine<R = RaRecord> {
  label: string;
  render: (record: R) => React.ReactNode;
}

export interface MobileCardSpec<R = RaRecord> {
  /** The value that identifies the row. */
  primary: (record: R) => React.ReactNode;
  /** The row's status mark, at the right of the first line. */
  status?: (record: R) => React.ReactNode;
  /** Two to four columns that drive a decision. */
  lines?: MobileCardLine<R>[];
  /** The row's actions (a `RowActionsMenu`). */
  actions?: (record: R) => React.ReactNode;
  /** What a tap on the card does. */
  onClick?: (record: R) => void;
}

export interface ResponsiveDatagridProps<R extends RaRecord = RaRecord> extends DatagridProps {
  mobile: MobileCardSpec<R>;
}

export interface RecordCardListProps<R> {
  /** The rows, already loaded. */
  records: R[];
  mobile: MobileCardSpec<R>;
  /** A stable key per row. */
  getKey: (record: R) => React.Key;
  /** Shown instead of the cards when there are no rows. */
  empty?: React.ReactNode;
}

/**
 * The phone form of a table whose rows do not come from a react-admin list (a
 * `DataTable` on a dashboard or a hand-fed list): the same card per row as
 * `ResponsiveDatagrid` draws.
 */
export const RecordCardList = <R,>({ records, mobile, getKey, empty = <DatagridEmpty /> }: RecordCardListProps<R>) => {
  const theme = useTheme();

  if (records.length === 0) return <>{empty}</>;

  return (
    <Stack spacing={1}>
      {records.map((record) => (
        <Card
          key={getKey(record)}
          onClick={mobile.onClick ? () => mobile.onClick!(record) : undefined}
          sx={{ p: 1.5, cursor: mobile.onClick ? 'pointer' : 'default' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography component="div" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                  {mobile.primary(record)}
                </Typography>
                {mobile.status && <Box sx={{ ml: 'auto', flexShrink: 0 }}>{mobile.status(record)}</Box>}
              </Box>
              {(mobile.lines ?? []).map((line) => (
                <Box key={line.label} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Typography component="span" variant="body2" sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}>
                    {line.label}
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {line.render(record)}
                  </Typography>
                </Box>
              ))}
            </Box>
            {mobile.actions && <Box onClick={(e) => e.stopPropagation()}>{mobile.actions(record)}</Box>}
          </Box>
        </Card>
      ))}
    </Stack>
  );
};

interface RecordCardsProps<R extends RaRecord> {
  mobile: MobileCardSpec<R>;
  empty: React.ReactNode;
}

const RecordCards = <R extends RaRecord>({ mobile, empty }: RecordCardsProps<R>) => {
  const { data, isPending } = useListContext<R>();

  if (isPending) return null;
  return <RecordCardList records={data ?? []} mobile={mobile} getKey={(record) => record.id} empty={empty} />;
};

export const ResponsiveDatagrid = <R extends RaRecord = RaRecord>({
  mobile,
  empty = <DatagridEmpty />,
  ...props
}: ResponsiveDatagridProps<R>) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  if (isPhone) return <RecordCards mobile={mobile} empty={empty} />;
  return <Datagrid bulkActionButtons={false} empty={empty} {...props} />;
};

export default ResponsiveDatagrid;
